import ApiService from "./api.service";
import PrivilegeService from "./privilege.service";
import I18nService from "./i18n.service";

const ParticipantService = {
  store: null,
  init(opts) {
    if (opts.store) {
      this.store = opts.store;
    }
  },

  getProfileCompletionText(participant) {
    const missingFields = participant?.requiredFieldsMissing || [];
    if (!missingFields.length) return I18nService.translate("Yes");

    const country = participant?.address?.country;
    const selectedCountry = this.store?.getters.selectedCountry;
    const missingFieldLabels = {
      firstName: "First Name",
      lastName: "Last Name",
      email1: "Email",
      phoneNumber1: "Phone Number",
      birthDate: "Date of Birth",
      gender: "Gender",
      address: "Address",
      address1: "Street Address",
      city: "City",
      region: country?.regionName || selectedCountry?.regionName || "State / Province",
      postalCode: country?.postalCodeName || selectedCountry?.postalCodeName || "Postal Code",
      country: "Country",
      tin: country?.participantTinName || selectedCountry?.participantTinName || "TIN",
      termsAndConditions: "Terms and Conditions Acceptance",
      preferredAwardVehicle: "Preferred Award Vehicle"
    };
    return missingFields
      .map(field => I18nService.translate(`Missing ${missingFieldLabels[field] || field}`))
      .join("; ");
  },

  /**
   * Determines if a participant is missing required information based on the settings configured at
   * the Participant Type level.
   * * Rules:
   * 1. If there are no missing required fields → return false.
   * 2. If the participant has a non-empty 'source' → consider it valid → return false.
   * 3. If the current user is not impersonating → any missing field is blocking → return true.
   * 4. If the user is impersonating and the only missing field is 'tin' → not blocking → return false.
   * 5. If the user is impersonating and there are other missing fields besides 'tin' → blocking → return true.
   * @returns {boolean} true if the participant is missing blocking information, false otherwise.
   */
  isMissingRequiredInformation() {
    const participant = this.store.getters.selectedParticipant;
    const missingFields = participant.requiredFieldsMissing || [];
    if (missingFields.length === 0) return false;

    // If the participant has a source value, we assume it's valid and skip further checks
    const hasSource = participant.source && participant.source.trim().length > 0;
    if (hasSource) return false;

    // If it's a regular user (not impersonating), any missing field is considered blocking
    const isImpersonating = PrivilegeService.isImpersonating();
    if (!isImpersonating) return true;

    const isOnlyMissingTin = missingFields.length === 1 && missingFields[0] === "tin";
    return !isOnlyMissingTin;
  },

  /**
   * Determines if a participant is missing security answers.  This method is smart enough to look at the
   * configuration of the program group and based on the number of security questions required, does the
   * user have the same number of answers.
   *
   * @returns
   */
  isMissingSecurityAnswers() {
    let isMissing = false;
    let participant = this.store.getters.selectedParticipant;
    if (participant) {
      let program = participant.program;
      if (program) {
        let programGroup = program.programGroup;
        if (programGroup) {
          let amountOfSecurityQuestionsForProgramGroup = programGroup.amountOfSecurityQuestions;
          if (amountOfSecurityQuestionsForProgramGroup > 0) {
            let securityAnswerCount = this.store.getters.securityAnswersCount;
            if (securityAnswerCount && securityAnswerCount >= amountOfSecurityQuestionsForProgramGroup) {
              isMissing = false;
            } else {
              isMissing = true;
            }
          }
        }
      }
    }
    if (isMissing) {
      console.log("Missing security answers");
    }
    return isMissing;
  },

  async logParticipant(participant, logMessage, user = null) {
    if (participant && participant.id) {
      await ApiService.post("/api/participants/" + participant.id + "/addActivity", {
        message: logMessage,
        user
      });
    }
  },
  checkParticipantChanges(initialParticipantData, currentData, paticipantType = null, organization = null) {
    const logs = [];
    if (paticipantType && paticipantType && initialParticipantData?.participantTypeName !== paticipantType?.name) {
      logs.push(
        `Participant Type changed from ${initialParticipantData?.participantTypeName} to ${paticipantType.name}`
      );
    }

    if (organization && organization && initialParticipantData?.organizationName !== organization?.name) {
      logs.push(`Organization changed from ${initialParticipantData.organizationName} to ${organization.name}`);
    }

    if (
      currentData?.status &&
      currentData?.status &&
      initialParticipantData?.status?.name !== currentData?.status?.name
    ) {
      logs.push(
        `Participant Status changed from ${initialParticipantData?.status?.description} to ${currentData?.status?.description}`
      );
    }

    if (initialParticipantData?.email !== currentData?.email) {
      logs.push(`Participant Email changed from ${initialParticipantData?.email} to ${currentData?.email}`);
    }

    if (initialParticipantData?.email1 !== currentData?.email1) {
      logs.push(`Participant Email changed from ${initialParticipantData?.email1} to ${currentData?.email1}`);
    }

    if (JSON.stringify(initialParticipantData?.address) !== JSON.stringify(currentData?.address)) {
      this.trackFieldChange(
        logs,
        initialParticipantData?.address?.address1,
        currentData?.address?.address1,
        "Address 1"
      );
      this.trackFieldChange(
        logs,
        initialParticipantData?.address?.address2,
        currentData?.address?.address2,
        "Address 2"
      );
      this.trackFieldChange(logs, initialParticipantData?.address?.city, currentData?.address?.city, "City");
      this.trackFieldChange(
        logs,
        initialParticipantData?.address?.region?.description,
        currentData?.address?.region?.description,
        "State"
      );
      this.trackFieldChange(
        logs,
        initialParticipantData?.address?.country?.name,
        currentData?.address?.country?.name,
        "Country"
      );
    }
    return logs;
  },
  trackFieldChange(logs, oldValue, newValue, displayName) {
    if (oldValue !== newValue) {
      logs.push(`Participant changed the ${displayName} from "${oldValue || ""}" to "${newValue || ""}"`);
    }
  }
};

export default ParticipantService;
